import type {
  EventDescriptionLanguage,
  EventDescriptionTranslations,
  EventMinAge,
  EventTicketUrlKind,
  LiveRoomMode,
} from '@pulsify/shared/dto'
import { useForm, useStore } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { CreditCard, History, Lock, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { usePlan } from '~/app/plan-context'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { eventImagesApi } from '~/entities/event/api'
import { registrationsApi } from '~/entities/event/api/registrations-api'
import {
  useCreateEvent,
  useEvent,
  usePublishEvent,
  useUnlistEvent,
} from '~/entities/event/model/use-events'
import { useListing, useListings } from '~/entities/listing/model/use-listings'
import { listingInScope, type ProfileScope } from '~/entities/profile/lib/profile-scope'
import { useProfileScope } from '~/entities/profile/model/use-profiles'
import { useEventGate } from '~/entities/subscription/model/use-event-gate'
import { type TicketTypeInput, ticketsApi } from '~/entities/ticket/api/tickets-api'
import { useTicketSettings } from '~/entities/ticket/model/use-tickets'
import {
  consumeAiEventHandoff,
  type HandoffLocationNotice,
  handoffLocationNotice,
  hasPendingAiEventHandoff,
  setAiEventHandoff,
} from '~/features/ai-studio/lib/event-handoff'
import { coverSubjectOf } from '~/features/events/ai-flow/cover-chat-button'
import { EventAiStart } from '~/features/events/ai-start/event-ai-start'
import type { RecurringConfig } from '~/features/events/components'
import {
  EventDateTimeSection,
  EventDetailsSection,
  EventFeaturesSection,
  EventImagesSection,
  EventLocationSection,
  EventPreviewModal,
} from '~/features/events/components'
import { EventEntrySection } from '~/features/events/components/event-entry-section'
import { EventFormLayout } from '~/features/events/components/event-form-layout'
import { applyAddressVisibility } from '~/features/events/lib/address-visibility'
import { coverFirst, hasEventDraft } from '~/features/events/lib/event-draft'
import {
  applyEventEntry,
  type EventEntry,
  entryFromHandoff,
  hasPaidTicketTypes,
  ticketTypesPrice,
} from '~/features/events/lib/event-entry'
import {
  applyEventPrivacy,
  type EventPrivacy,
  initialEventPrivacy,
  publishActionFor,
} from '~/features/events/lib/event-privacy'
import { useEventDraft } from '~/features/events/lib/use-event-draft'
import { ConfirmButton } from '~/features/events/tickets/confirm-button'
import type { ImageFile } from '~/features/shared/components/images-section'
import { useLeaveGuard } from '~/features/shared/components/leave-guard'
import { useAfterSaveAction } from '~/features/shared/hooks/use-after-save-action'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { normalizeCountry } from '~/shared/lib/countries'
import { focusFirstInvalidField, submitForm } from '~/shared/lib/form-utils'
import { log } from '~/shared/lib/logger'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import type { Event, OccurrenceForm } from '~/shared/types/event'
import type { Listing } from '~/shared/types/listing'
import { Button } from '~/shared/ui/button'
import { Card, CardContent } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'
import { PageHeader } from '~/widgets/page-header'

export const Route = createFileRoute('/dashboard/events/create')({
  validateSearch: (search) => ({
    copyFrom: search.copyFrom as string | undefined,
    source: search.source as string | undefined,
    fromListing: search.fromListing as string | undefined,
    manual: search.manual === true || search.manual === 'true' ? true : undefined,
  }),
  component: CreateEventRoute,
})

type PinnedPoint = { lat: number; lng: number }

const QUIET = { dontUpdateMeta: true } as const

function CreateEventSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48 max-w-full" />
          <Skeleton className="h-4 w-72 max-w-full" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-24" />
        </div>
      </div>
      <div className="mx-auto grid w-full max-w-7xl gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)]">
        <div className="space-y-6">
          <Skeleton className="h-96 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
        <Skeleton className="hidden h-96 w-full rounded-lg xl:block" />
      </div>
    </div>
  )
}

function CreateEventRoute() {
  const { atLimit } = useEventGate()

  if (atLimit) {
    return <CreateEventLimitGuard />
  }

  return <CreateEventRouteContent />
}

function CreateEventLimitGuard() {
  return (
    <div className="space-y-6">
      <PageHeader title={m.createEvent()} description={m.addANewEventForYourBusiness()} />
      <Card className="border-amber-300 dark:border-amber-700">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Lock className="h-16 w-16 text-amber-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">{m.subscriptionLimitReached()}</h2>
          <p className="text-muted-foreground text-center mb-6 max-w-md">
            {m.eventLimitReachedCreateBlocked()}
          </p>
          <Link to="/dashboard/subscription">
            <Button className="gap-2">
              <CreditCard className="w-4 h-4" />
              {m.upgradeNow()}
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  )
}

function CreateEventRouteContent() {
  const { copyFrom, source, fromListing: requestedListing, manual } = Route.useSearch()
  const navigate = useNavigate()
  const { data: business } = useCurrentBusiness()
  const { scope, isLoading: isScopeLoading } = useProfileScope()
  const fromListing = listingInScope(requestedListing, scope) ? requestedListing : undefined
  const { data: sourceEvent, isPending: isEventPending } = useEvent(
    business?.id ?? '',
    copyFrom ?? '',
  )
  const { data: fromListingData, isLoading: isListingLoading } = useListing(
    business?.id ?? '',
    fromListing ?? '',
  )
  const [formKey, setFormKey] = useState(0)
  if (isScopeLoading) return <CreateEventSkeleton />
  const resumeDraft = !copyFrom && !!business && hasEventDraft(business.id)
  if (!copyFrom && source !== 'ai' && !manual && !resumeDraft) {
    if (!business) return <CreateEventSkeleton />
    return (
      <EventAiStart
        businessId={business.id}
        fromListing={fromListing}
        onDrafted={(handoff) => {
          setAiEventHandoff(handoff)
          navigate({ to: '/dashboard/events/create', search: { source: 'ai', fromListing } })
        }}
      />
    )
  }
  // isPending (v5) covers both "disabled" (no businessId yet) and "fetching" states.
  if (copyFrom && (!business || isEventPending)) {
    return <CreateEventSkeleton />
  }
  if (fromListing && (!business || isListingLoading)) {
    return <CreateEventSkeleton />
  }

  return (
    <CreateEventForm
      key={`${copyFrom ? (sourceEvent?.id ?? 'new') : (fromListing ?? 'new')}-${formKey}`}
      onDiscard={() => {
        setFormKey((key) => key + 1)
        navigate({
          to: '/dashboard/events/create',
          search: { manual: manual || undefined, fromListing },
          replace: true,
        })
      }}
      copyFrom={copyFrom}
      sourceEvent={sourceEvent}
      fromAi={source === 'ai'}
      fromListing={fromListing}
      fromListingData={fromListing ? fromListingData : undefined}
      scope={scope}
    />
  )
}

function CreateEventForm({
  onDiscard,
  copyFrom,
  sourceEvent,
  fromAi,
  fromListing,
  fromListingData,
  scope,
}: {
  onDiscard: () => void
  copyFrom?: string
  sourceEvent?: Event
  fromAi?: boolean
  fromListing?: string
  fromListingData?: Listing
  scope: ProfileScope
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: business } = useCurrentBusiness()
  const createEvent = useCreateEvent(business?.id || '')
  const publishEvent = usePublishEvent(business?.id || '')
  const unlistEvent = useUnlistEvent(business?.id || '')
  const { listings: allListings } = useListings(business?.id || '')
  const { data: profilePlace } = useListing(
    business?.id ?? '',
    scope.kind === 'place' ? scope.listingId : '',
  )
  const listings = scope.kind === 'organization' ? allListings : profilePlace ? [profilePlace] : []
  const hostProfileId = scope.kind === 'organization' ? null : scope.profileId
  const { limits } = usePlan()
  const canRecur = limits.canRecurEvents
  const [images, setImages] = useState<ImageFile[]>([])
  const [primaryImageIndex, setPrimaryImageIndex] = useState(0)
  const [aiImages, setAiImages] = useState<ImageFile[]>([])
  const [isUploadingImages, setIsUploadingImages] = useState(false)
  const sourceImages = copyFrom && sourceEvent?.images ? sourceEvent.images : []
  const hasImages = images.length > 0 || sourceImages.length > 0
  const [manualListingId, setManualListingId] = useState<string | null | undefined>(
    fromListing && fromListingData
      ? fromListing
      : copyFrom && sourceEvent
        ? sourceEvent.listingId && !listingInScope(sourceEvent.listingId, scope)
          ? null
          : sourceEvent.listingId
        : undefined,
  )
  const [recurringConfig, setRecurringConfig] = useState<RecurringConfig>({
    enabled: false,
    frequencyType: 'weeks',
    interval: 1,
    count: 5,
  })
  const afterSave = useAfterSaveAction<'publish' | 'unlist'>()
  const consumedAiHandoffRef = useRef(false)
  const { data: ticketSettings } = useTicketSettings(business?.id ?? '')
  const [aiScheduleKey, setAiScheduleKey] = useState<string | null>(null)
  const [autoSave, setAutoSave] = useState<{ images: number } | null>(null)
  const [locationNotice, setLocationNotice] = useState<HandoffLocationNotice>(null)
  const selectedListingId =
    manualListingId === undefined ? (listings[0]?.id ?? null) : manualListingId

  const handleImagesChange = useCallback(
    (newImages: ImageFile[], newPrimaryIndex: number, _newInsertPositions?: number[]) => {
      setImages(newImages)
      setPrimaryImageIndex(newPrimaryIndex)
    },
    [],
  )

  // The AI pictures arrive after mount; seed them through ImagesSection's
  // initialNewImages (which renders the thumbnails) rather than the parent-only
  // images state, and remount the section via key so it re-reads the seed.
  const initialNewImages = aiImages

  const defaultValues =
    copyFrom && sourceEvent
      ? {
          title: sourceEvent.title,
          description: sourceEvent.description,
          descriptionLanguage: sourceEvent.descriptionLanguage,
          descriptionTranslations: sourceEvent.descriptionTranslations,
          location: {
            name: sourceEvent.location.name ?? '',
            street: sourceEvent.location.street,
            city: sourceEvent.location.city,
            country: normalizeCountry(sourceEvent.location.country),
            coordinates: sourceEvent.location.coordinates as PinnedPoint | undefined,
          },
          locationPending: sourceEvent.locationPending ?? false,
          addressHidden: sourceEvent.addressHidden ?? false,
          startTime: '',
          endTime: '',
          occurrences: [] as OccurrenceForm[],
          price: sourceEvent.price,
          ticketUrl: sourceEvent.ticketUrl ?? '',
          ticketUrlKind: (sourceEvent.ticketUrlKind ?? 'tickets') as EventTicketUrlKind,
          capacity: sourceEvent.capacity ?? undefined,
          rsvpEnabled: sourceEvent.rsvpEnabled,
          liveRoomMode: sourceEvent.liveRoomMode,
          plusOnesMax: sourceEvent.plusOnesMax,
          privacy: initialEventPrivacy(sourceEvent),
          requestEmails: sourceEvent.requestEmails,
          minAge: sourceEvent.minAge as EventMinAge | null,
          studentsOnly: sourceEvent.studentsOnly,
          audienceNote: sourceEvent.audienceNote ?? '',
          targetAgeMin: sourceEvent.targetAgeMin ?? undefined,
          targetAgeMax: sourceEvent.targetAgeMax ?? undefined,
          tags: {
            mainCategoryId: sourceEvent.tags.category.id,
            subCategoryTags: sourceEvent.tags.subCategories.map((t) => t.id),
            subSubCategoryTags: sourceEvent.tags.subSubCategories.map((t) => t.id),
            amenityIds: sourceEvent.tags.amenities.map((a) => a.id),
          },
          listingId: sourceEvent.listingId,
          hostProfileId,
          entry: entryFromHandoff({
            signup: false,
            ticketUrl: sourceEvent.ticketUrl ?? '',
            price: sourceEvent.price,
          }) as EventEntry | null,
          ticketTypes: [] as TicketTypeInput[],
        }
      : {
          title: '',
          description: '',
          descriptionLanguage: 'de' as EventDescriptionLanguage,
          descriptionTranslations: {} as EventDescriptionTranslations,
          location: fromListingData
            ? {
                name: fromListingData.location.name || fromListingData.name || '',
                street: fromListingData.location.street,
                city: fromListingData.location.city,
                country: normalizeCountry(fromListingData.location.country),
                coordinates: fromListingData.location.coordinates as PinnedPoint | undefined,
              }
            : {
                name: '',
                street: '',
                city: '',
                country: 'Austria',
                coordinates: undefined as PinnedPoint | undefined,
              },
          locationPending: false,
          addressHidden: false,
          startTime: '',
          endTime: '',
          occurrences: [] as OccurrenceForm[],
          price: {
            kind: 'unknown' as const,
          },
          ticketUrl: '',
          ticketUrlKind: 'tickets' as EventTicketUrlKind,
          capacity: undefined as number | undefined,
          rsvpEnabled: false,
          liveRoomMode: 'open' as LiveRoomMode,
          plusOnesMax: 0,
          privacy: 'public' as EventPrivacy,
          requestEmails: true,
          minAge: null as EventMinAge | null,
          studentsOnly: false,
          audienceNote: '',
          targetAgeMin: undefined as number | undefined,
          targetAgeMax: undefined as number | undefined,
          tags: {
            mainCategoryId: '',
            subCategoryTags: [],
            subSubCategoryTags: [],
            amenityIds: [],
          },
          listingId: (fromListingData ? fromListing : null) as string | null,
          hostProfileId,
          entry: null as EventEntry | null,
          ticketTypes: [] as TicketTypeInput[],
        }

  const form = useForm({
    defaultValues,
    onSubmitInvalid: ({ formApi }) => {
      afterSave.consume()
      focusFirstInvalidField(formApi.state.fieldMeta)
    },
    onSubmit: async ({ value: formValue }) => {
      const afterSaveAction = afterSave.consume()
      const value = applyEventPrivacy(applyAddressVisibility(applyEventEntry(formValue), false))

      try {
        const selectedListing = value.listingId
          ? listings.find((l) => l.id === value.listingId)
          : null

        const listingLocation = selectedListing?.location || null

        let created: { id: string }
        try {
          created = await createEvent.mutateAsync({ data: value, listingLocation })
          draft.clear()
          leaveGuard.release()
        } catch (error) {
          log.error(
            error instanceof Error ? error : new Error(String(error)),
            'Failed to create event',
          )
          toast.error(m.failedToCreateEvents(), { description: getErrorMessageFromError(error) })
          return
        }

        if (value.entry === 'signup' && business?.id) {
          try {
            await registrationsApi.save(business.id, created.id, {
              enabled: true,
              questions: [],
              seatLimit: value.capacity ?? null,
              waitlist: true,
              deadline: null,
            })
          } catch (signupError) {
            log.error(
              signupError instanceof Error ? signupError : new Error(String(signupError)),
              '[CreateEvent] Failed to switch on the sign-up',
            )
          }
        }

        if (value.entry === 'tickets' && business?.id) {
          try {
            for (const [sort, input] of value.ticketTypes.entries()) {
              await ticketsApi.createType(business.id, created.id, { ...input, sort })
            }
          } catch (ticketError) {
            log.error(
              ticketError instanceof Error ? ticketError : new Error(String(ticketError)),
              '[CreateEvent] Failed to add the ticket types',
            )
            toast.warning(m.entryTicketsFailed())
          }
        }
        const needsPayouts =
          value.entry === 'tickets' &&
          hasPaidTicketTypes(value.ticketTypes) &&
          !ticketSettings?.connect.onboardingComplete
        const leave = () =>
          needsPayouts
            ? navigate({ to: '/dashboard/events/$eventId', params: { eventId: created.id } })
            : navigate({ to: '/dashboard/events' })

        if (hasImages && created.id) {
          setIsUploadingImages(true)
          try {
            if (copyFrom) {
              await eventImagesApi.copyFromEvent(created.id, copyFrom)
            }
            if (images.length > 0) {
              await eventImagesApi.uploadMultiple(
                created.id,
                images.map((img) => img.file),
              )
            }
            toast.success(m.eventCreatedWithImages(), {
              description: m.yourEventAndImagesHaveBeenUploadedSuccessfully(),
            })
          } catch (imageError) {
            log.error(
              imageError instanceof Error ? imageError : new Error(String(imageError)),
              'Failed to upload images',
            )
            toast.warning(m.eventCreatedButSomeImagesFailedToUpload(), {
              description: m.youCanAddImagesLaterFromTheEventDetailsPage(),
            })
          } finally {
            setIsUploadingImages(false)
            await queryClient.invalidateQueries({
              queryKey: queryKey(QueryKey.EVENTS, business?.id),
            })
          }
        } else {
          toast.success(m.eventCreated(), {
            description: m.yourEventHasBeenCreatedSuccessfully(),
          })
        }

        if (afterSaveAction === 'publish') {
          try {
            await publishEvent.mutateAsync(created.id)
            toast.success(m.eventPublished())
            leave()
          } catch (publishError) {
            log.error(
              publishError instanceof Error ? publishError : new Error(String(publishError)),
              '[CreateEvent] Failed to publish after create',
            )
            toast.error(m.failedToPublishEvent(), {
              description: getErrorMessageFromError(publishError),
            })
            navigate({ to: '/dashboard/events/$eventId/edit', params: { eventId: created.id } })
          }
        } else if (afterSaveAction === 'unlist') {
          try {
            await unlistEvent.mutateAsync(created.id)
            toast.success(m.eventUnlisted())
            leave()
          } catch (unlistError) {
            log.error(
              unlistError instanceof Error ? unlistError : new Error(String(unlistError)),
              '[CreateEvent] Failed to unlist after create',
            )
            toast.error(m.failedToUnlistEvent(), {
              description: getErrorMessageFromError(unlistError),
            })
            navigate({ to: '/dashboard/events/$eventId/edit', params: { eventId: created.id } })
          }
        } else {
          leave()
        }
      } catch (error) {
        toast.error(getErrorMessageFromError(error))
      }
    },
  })

  const [skipDraftRestore] = useState(() => !!fromAi && hasPendingAiEventHandoff())
  const draftImages = useMemo(
    () => coverFirst(images, primaryImageIndex),
    [images, primaryImageIndex],
  )
  const draft = useEventDraft({
    businessId: business?.id ?? '',
    form,
    enabled: !copyFrom,
    skipRestore: skipDraftRestore,
    images: draftImages,
    listingId: manualListingId,
    onRestore: ({ values, listingId }) => {
      if (listingId !== undefined) setManualListingId(listingId)
      const firstDay = (values.occurrences as OccurrenceForm[] | undefined)?.[0]?.date
      if (firstDay) setAiScheduleKey(`draft-${firstDay}`)
    },
    onRestoreImages: setAiImages,
  })
  const dirty = useStore(form.store, (state) => state.isDirty)
  const leaveGuard = useLeaveGuard(dirty, copyFrom ? m.leaveUnsavedBody() : m.leaveDraftKeptBody())

  const handleCancel = () => {
    navigate({ to: '/dashboard/events' })
  }

  const discardDraft = () => {
    draft.clear()
    leaveGuard.release()
    onDiscard()
  }

  useEffect(() => {
    form.setFieldValue('listingId', selectedListingId, QUIET)
    if (!selectedListingId) return
    if (copyFrom) return
    const listing = listings.find((l) => l.id === selectedListingId)
    if (!listing) return
    form.setFieldValue('location.name', listing.location.name || listing.name || '', QUIET)
    form.setFieldValue('location.street', listing.location.street, QUIET)
    form.setFieldValue('location.city', listing.location.city, QUIET)
    form.setFieldValue('location.country', normalizeCountry(listing.location.country), QUIET)
    form.setFieldValue('location.coordinates', listing.location.coordinates, QUIET)
  }, [selectedListingId, copyFrom, form.setFieldValue, listings.find])

  useEffect(() => {
    if (consumedAiHandoffRef.current) return
    if (!fromAi) return
    consumedAiHandoffRef.current = true
    const handoff = consumeAiEventHandoff()
    if (!handoff) return
    const v = handoff.values
    form.setFieldValue('title', v.title)
    form.setFieldValue('description', v.description)
    if (handoff.descriptionLanguage) {
      form.setFieldValue('descriptionLanguage', handoff.descriptionLanguage)
    }
    if (handoff.descriptionTranslations) {
      form.setFieldValue('descriptionTranslations', handoff.descriptionTranslations)
    }
    if (handoff.features) {
      form.setFieldValue('liveRoomMode', handoff.features.liveRoom ? 'open' : 'off')
    }
    // Gated on a usable schedule: the top-level range is rendered by no field
    // on this page, so seeding it without the occurrences the date section does
    // render would save the event on a date the user never saw.
    if (v.occurrences.length > 0) {
      form.setFieldValue('startTime', v.startTime)
      form.setFieldValue('endTime', v.endTime)
      form.setFieldValue('occurrences', v.occurrences)
      setAiScheduleKey(v.occurrences[0].date)
    }
    form.setFieldValue('price', v.price)
    form.setFieldValue('ticketUrl', v.ticketUrl)
    form.setFieldValue('ticketUrlKind', v.ticketUrlKind)
    const entry =
      handoff.entry === undefined
        ? entryFromHandoff({
            signup: handoff.features?.signup ?? false,
            ticketUrl: v.ticketUrl,
            price: v.price,
          })
        : handoff.entry
    form.setFieldValue('entry', entry)
    form.setFieldValue('ticketTypes', v.ticketTypes)
    if (entry === 'tickets') form.setFieldValue('price', ticketTypesPrice(v.ticketTypes))
    form.setFieldValue('privacy', v.privacy)
    form.setFieldValue('minAge', v.minAge)
    form.setFieldValue('studentsOnly', v.studentsOnly)
    form.setFieldValue('targetAgeMin', v.targetAgeMin)
    form.setFieldValue('targetAgeMax', v.targetAgeMax)
    form.setFieldValue('audienceNote', v.audienceNote)
    form.setFieldValue('locationPending', v.locationPending)
    form.setFieldValue('addressHidden', v.addressHidden)
    if (v.capacity !== undefined) form.setFieldValue('capacity', v.capacity)
    form.setFieldValue('rsvpEnabled', false)
    form.setFieldValue('tags', v.tags)
    setLocationNotice(handoffLocationNotice(v))
    if (v.location) {
      setManualListingId(v.listingId && !listingInScope(v.listingId, scope) ? null : v.listingId)
      form.setFieldValue('location.name', v.location.name ?? '')
      form.setFieldValue('location.street', v.location.street)
      form.setFieldValue('location.city', v.location.city)
      if (v.location.country) {
        form.setFieldValue('location.country', normalizeCountry(v.location.country))
      }
      form.setFieldValue('location.coordinates', v.location.coordinates ?? undefined)
    } else {
      setManualListingId(null)
      form.setFieldValue('location.name', '')
      form.setFieldValue('location.street', '')
      form.setFieldValue('location.city', '')
      form.setFieldValue('location.coordinates', undefined)
    }
    if (handoff.images.length > 0) {
      setAiImages(handoff.images)
    }
    if (handoff.autoSave) setAutoSave({ images: handoff.images.length })
  }, [fromAi, form.setFieldValue, scope])

  useEffect(() => {
    if (!autoSave || images.length < autoSave.images) return
    setAutoSave(null)
    submitForm(form)
  }, [autoSave, images.length, form])

  const coverImageUrl =
    images.length > 0 ? images[primaryImageIndex]?.preview : sourceImages[0]?.url || ''

  return (
    <div className="space-y-6">
      <div className="mx-auto w-full max-w-7xl">
        <PageHeader
          title={m.createEvent()}
          description={m.addANewEventForYourBusiness()}
          action={
            <div className="flex flex-wrap gap-2">
              <div className="xl:hidden">
                <form.Subscribe
                  selector={(state) => ({
                    title: state.values.title,
                    description: state.values.description,
                    startTime: state.values.occurrences[0]?.date || state.values.startTime,
                    location: state.values.location,
                    price: state.values.price,
                  })}
                >
                  {({ title, description, startTime, location, price }) => (
                    <EventPreviewModal
                      title={title}
                      description={description}
                      startTime={startTime}
                      location={location}
                      price={price}
                      coverImageUrl={coverImageUrl}
                    />
                  )}
                </form.Subscribe>
              </div>
              <Button variant="outline" className="sm:hidden" onClick={handleCancel}>
                {m.cancel()}
              </Button>
            </div>
          }
        />
      </div>
      {draft.restored ? (
        <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center gap-2 rounded-lg border bg-muted px-4 py-2 text-sm">
          <History aria-hidden className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1">{m.eventDraftRestored()}</span>
          <ConfirmButton
            trigger={
              <Button type="button" variant="ghost" size="sm">
                {m.eventDraftDiscard()}
              </Button>
            }
            title={m.eventDraftDiscardTitle()}
            body={m.eventDraftDiscardBody()}
            confirmLabel={m.eventDraftDiscard()}
            onConfirm={discardDraft}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={m.close()}
            onClick={draft.hideRestoredNote}
          >
            <X />
          </Button>
        </div>
      ) : null}
      {leaveGuard.dialog}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          submitForm(form)
        }}
      >
        <EventFormLayout
          form={form}
          coverImageUrl={coverImageUrl}
          onCancel={handleCancel}
          actions={({ isSubmitting, privacy }) => {
            const isDisabled = isSubmitting || createEvent.isPending || isUploadingImages
            const isBusy = createEvent.isPending || isSubmitting
            const goLive = publishActionFor(privacy)
            return {
              primary: {
                label: goLive === 'unlist' ? m.publishPrivately() : m.saveAndPublish(),
                busyLabel: m.savingAndPublishing(),
                busy: isBusy && afterSave.action !== null,
                disabled: isDisabled,
                blockedHint: hasImages ? undefined : m.addACoverImageToPublish(),
                onClick: () => afterSave.trigger(form, goLive),
              },
              secondary: {
                label: m.saveAsDraft(),
                busyLabel: isUploadingImages ? m.uploadingImages() : m.creating(),
                busy: (isBusy && afterSave.action === null) || isUploadingImages,
                disabled: isDisabled,
                onClick: () => submitForm(form),
              },
            }
          }}
        >
          <EventImagesSection
            key={aiImages.length > 0 ? `ai-${aiImages[0].id}` : 'images'}
            existingImages={sourceImages}
            initialNewImages={initialNewImages}
            onImagesChange={handleImagesChange}
            coverSubject={() => coverSubjectOf(form.state.values)}
          />
          <EventDetailsSection form={form} />
          <EventDateTimeSection
            // The AI schedule arrives after mount, but the section seeds its
            // from/to pickers from the occurrences once on mount. Remount it
            // so the generated dates show up in the pickers, not just as rows.
            key={aiScheduleKey ? `ai-${aiScheduleKey}` : 'dates'}
            form={form}
            recurringConfig={recurringConfig}
            onRecurringConfigChange={setRecurringConfig}
            canRecur={canRecur}
          />
          <EventLocationSection
            form={form}
            listings={listings}
            selectedListingId={selectedListingId}
            onListingChange={(listingId) => {
              setManualListingId(listingId)
            }}
            notice={locationNotice}
          />
          <EventEntrySection form={form} businessId={business?.id ?? ''} />
          <EventFeaturesSection form={form} collapsible />
        </EventFormLayout>
      </form>
    </div>
  )
}
